import { faker } from "@faker-js/faker";

import {
  Event,
  EventAllowedSubscription,
  EventsLabels,
  EventSubscription,
  EventSubscriptionMethod,
  EventType,
} from "../components/events/types";

export function mockSubscriptionType(subscriptionData: Partial<EventAllowedSubscription> = {}) {
  return {
    soldOut: faker.datatype.boolean(),
    subscribeUrl: faker.internet.url(),
    subscriptionMethod: faker.number.int({ min: 0, max: 1 }),
    subscriptionType: faker.number.int({ min: 0, max: 1 }),
    title: faker.company.catchPhrase(),
    ...subscriptionData,
  } satisfies EventAllowedSubscription;
}

const mockEventType = () =>
  [EventType.DIGITAL, EventType.IN_PERSON, EventType.BOTH][faker.number.int({ max: 2 })];

export function mockEventData(eventData: Partial<Event> = {}) {
  const startDateEvent = faker.date.soon({ days: 60 }).toISOString();
  const endDateEvent = faker.date.soon({ refDate: startDateEvent, days: 15 }).toISOString();
  const startDateSubscription = faker.date
    .between({ from: startDateEvent, to: endDateEvent })
    .toISOString();
  return {
    id: faker.number.int({ min: 1, max: 9999 }),
    startDateEvent,
    endDateEvent,
    startDateSubscription,
    endDateSubscription: faker.date
      .between({ from: startDateSubscription, to: endDateEvent })
      .toISOString(),
    title: faker.company.catchPhrase(),
    materials: ["Wood", "Glass", "Stone", "Metal", "Advanced materials"]
      .slice(0, faker.number.int({ min: 1, max: 5 }))
      .map((material) => ({ Name: material })),
    location: faker.location.city(),
    country: faker.location.country(),
    eventDescription: faker.lorem.paragraph(),
    eventType: mockEventType(),
    url: faker.internet.url(),
    urlImg: faker.image.url({ width: 1920, height: 1080 }),
    subscriptionTypes: [
      {
        soldOut: faker.datatype.boolean(),
        subscribeUrl: faker.internet.url(),
        subscriptionMethod: faker.number.int({ min: 0, max: 1 }),
        subscriptionType: faker.number.int({ min: 0, max: 1 }),
        title: faker.company.catchPhrase(),
      },
    ],
    videoContent: faker.datatype.boolean(),
    ...eventData,
  } satisfies Event;
}

export const mockSubscription = (subscriptionData: Partial<EventSubscription> = {}) =>
  ({
    checkInDate: faker.date.recent().toISOString(),
    checkOutDate: faker.date.recent().toISOString(),
    endDate: "",
    eventId: faker.number.int({ max: 999 }),
    eventType: mockEventType(),
    hotel: faker.datatype.boolean(),
    hotelDescription: faker.lorem.sentence(),
    id: faker.number.int({ max: 999 }),
    materials: [],
    paidEvent: faker.datatype.boolean(),
    registrationCancelled: false,
    registrationConfirmed: true,
    rooms: "",
    startDate: "",
    subscriptionMethod: [
      EventSubscriptionMethod.DIGITAL_SUBSCRIPTION,
      EventSubscriptionMethod.IN_PERSON_SUBSCRIPTION,
    ][faker.number.int({ max: 1 })],
    title: faker.company.catchPhrase(),
    urlSubscription: faker.internet.url(),
    people: [
      {
        Company: "Biesse",
        FirstName: "Mario",
        LastName: "Rossi",
      },
    ],
    ...subscriptionData,
  }) satisfies EventSubscription;

export const mockEventsLabels: EventsLabels = {
  "event-sold-out": "L'evento è sold out",
  "events-fetch-error":
    "Errore durante il caricamento eventi. Per favore, ricarica la pagina per provare di nuovo.",
  "events-no-results": "Nessun evento trovato",
  "events-filters-clear-button": "Azzera filtri",
  "events-filters-country": "Paese",
  "events-filters-all-countries": "Tutti i paesi",
  "event-detail-fetch-error": "Evento non trovato",
  "event-type": "Tipo evento",
  "event-type-in-presence": "In presenza",
  "event-type-online": "Online",
  "event-go-to-details": "Vai all'evento",
  "event-location": "Luogo",
  "event-description": "Descrizione evento",
  "event-subscribe-cta-both":
    'Per partecipare all’evento, sia in modalità "In presenza" che "Online"',
  "event-subscribe-cta-online": 'Per partecipare all’evento in modalità "Online"',
  "event-subscribe-cta-in-presence": 'Per partecipare all’evento in modalità "In presenza"',
  "event-participate-cta-both":
    'Per partecipare all’evento "Online" e per sapere maggiori dettagli sulla prenotazione "In presenza"',
  "event-participate-cta-online": 'Per partecipare all’evento "Online"',
  "event-participate-cta-in-presence":
    'Per sapere maggiori dettagli sulla prenotazione "In presenza"',
  "event-subscribe-button": "Iscriviti all'evento",
  "event-subscribe-modal-title": "Scegli la modalità dell'iscrizione all'evento",
  "event-subscribe-in-presence-button": "Iscriviti per partecipare in presenza",
  "event-subscribe-online-button": "Iscriviti per partecipare online",
  "event-already-subscribed-question": "Sei già iscritto all'evento?",
  "event-already-subscribed-logged": "Sei già iscritto a questo evento",
  "event-past-relive-with-video": "Questo evento è passato, ma lo puoi rivivere guardando il video",
  "event-past-relive-without-video": "Questo evento è passato",
  "event-past-relive-header": "Rivivi l'evento",
  "event-past-relive-cta": "Guarda il video",
  "event-sign-in": "Accedi",
  "event-go-to-agenda": "Vai nell'agenda",
  "event-subscriptions-open": "Le iscrizione sono aperte",
  "event-subscriptions-closed": "Le iscrizione sono chiuse",
  "event-subscription-sold-out": "Sold out",
  "events-filters-toggle-button": "Filtra",
  "events-filters-submit-button": "Cerca",
  "events-filters-materials": "Materiali",
  "events-filters-search": "Cerca tra gli eventi",
  "events-filters-show-past": "Eventi passati",
  "events-filters-show-current-future": "Eventi presenti e futuri",
  "events-load-more": "Mostra di più",
  "common-countdown-days": "Gior",
  "common-countdown-hours": "Ore",
  "common-countdown-minutes": "Min",
  "common-countdown-seconds": "Sec",
  glass: "Vetro",
  materia: "Plastica",
  materials: "Materiali",
  stone: "Pietra",
  wood: "Legno",
  metal: "Metallo",
  otherMaterial: "Altri materiali",
};
